import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { agencyQuery } from '../adb/agencies';
import { astronautQuery } from '../adb/astronauts';
import { nationQuery } from '../adb/nations';
import { assetQuery, sanityFetch } from '../services/sanity';
import { countdownToDate, zeroPad } from '../utils/date';
// ---------- FUNCTIONS ----------
export const getLaunch = async (config, launchId) => {
    return sanityFetch(config, `*[
    _type == 'launch'
    && _id == '${launchId}'
  ][0]${launchQuery}`);
};
export const getLaunchBySlug = async (config, slug) => {
    return sanityFetch(config, `*[
    _type == 'launch'
    && slug.current == '${slug}'
  ][0]${launchQuery}`);
};
export const getUpcomingLaunches = async (config) => {
    const now = new Date();
    const date = new Date(now.getTime() - 1000 * 60 * 60);
    const launches = await sanityFetch(config, `*[
      _type == 'launch'
      && !(_id in path("drafts.**"))
      && launchInfo.launchDate.utc > "${date.toISOString()}"
    ] | order(launchInfo.launchDate.utc asc) ${launchQuery}`);
    return launches;
};
export const getNextLaunch = async (config) => {
    const now = new Date();
    const date = new Date(now.getTime() - 1000 * 60 * 60);
    const launches = await sanityFetch(config, `*[
      _type == 'launch'
      && !(_id in path("drafts.**"))
      && launchInfo.launchDate.utc > "${date.toISOString()}"
    ] | order(launchInfo.launchDate.utc asc) [0...1] ${launchQuery}`);
    const results = launches;
    if (results && results.length > 0) {
        return results[0];
    }
    else {
        return undefined;
    }
};
export const getPastLaunches = async (config) => {
    const now = new Date();
    const date = new Date(now.getTime() - 1000 * 60 * 60);
    const archiveLaunchesLimit = 10000;
    const launches = await sanityFetch(config, `*[
      _type == 'launch'
      && !(_id in path("drafts.**"))
      && launchInfo.launchDate.utc < "${date.toISOString()}"
    ] | order(launchInfo.launchDate.utc desc)
    [0...${archiveLaunchesLimit}] ${launchQuery}`);
    return launches;
};
export const useLaunch = (config, slug, _launch, isFocused = true) => {
    const [launch, setLaunch] = useState(_launch);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!slug) {
            setLoading(false);
            return;
        }
        if (!launch) {
            setLoading(true);
            getLaunchBySlug(config, slug).then((l) => {
                l && setLaunch(l);
                setLoading(false);
            });
        }
        const interval = setInterval(() => {
            getLaunchBySlug(config, slug).then((l) => {
                l && setLaunch(l);
                setLoading(false);
            });
        }, 20000);
        return () => clearInterval(interval);
    }, [isFocused, slug]);
    return { launch, loading };
};
export const useNextLaunch = (config) => {
    const [launch, setLaunch] = useState();
    useEffect(() => {
        getNextLaunch(config).then((l) => {
            setLaunch(l);
        });
        const interval = setInterval(() => {
            getNextLaunch(config).then((l) => {
                setLaunch(l);
            });
        }, 10000);
        return () => clearInterval(interval);
    }, []);
    return launch;
};
export const useCountdownToLaunch = (launch, isFocused = true) => {
    const [countdown, setCountdown] = useState();
    useEffect(() => {
        if (!launch)
            return;
        setCountdown(countdownToLaunch(launch));
        const interval = setInterval(() => {
            setCountdown(countdownToLaunch(launch));
        }, 1000);
        return () => clearInterval(interval);
    }, [isFocused, launch]);
    return countdown;
};
export const deriveLaunchDescription = (launch, countdownComponents) => {
    if (!launch || !countdownComponents) {
        return '';
    }
    const { isPast, isVague } = countdownComponents;
    let description = isPast
        ? launch.launchInfo.launchDescriptionPast || ''
        : launch.launchInfo.launchDescriptionExtended || '';
    const timeToRaw = DateTime.fromISO(launch.launchInfo.launchDate?.utc || '').toRelative() ||
        '';
    let timeTo = timeToRaw.charAt(0).toUpperCase() + timeToRaw.slice(1);
    if (!isPast && !isVague) {
        timeTo = `${timeTo}, `;
    }
    if (isVague) {
        timeTo = '';
        if (description.substring(0, 2) === 'on') {
            description = `O${description.substring(1, description.length)}`;
        }
    }
    return `${isVague ? '' : timeTo}${description}`;
};
// ---------- QUERIES ----------
export const launchQuery = `{
  _id,
  _createdAt,
  title,
  headline,
  slug,
  launchInfo,
  image ${assetQuery},
  launchModules[]{
    _key,
    _type,
    customTitle,
    customImage ${assetQuery},
    module->{
      _type,
      slug,
      title,
      description,
      image ${assetQuery},
      IATA,
      airportName,
      viewingLocations[]{
        _key,
        name,
        image ${assetQuery},
        location,
        hours,
        cost,
        type,
        description
      },
      launchPad,
      launchLat,
      launchLong,
      guideDownload,
      know
    }
  },
  nations[]->${nationQuery},
  agencies[]->${agencyQuery},
  astronauts[]->${astronautQuery},
  tags[]->,
  tbdAstronauts
}`;
export const deriveLaunchState = (isoDateString) => {
    if (isoDateString === undefined || isoDateString.length === 0) {
        console.error('Bad date sent to getShortDisplayTime!');
    }
    const total = Date.parse(isoDateString || '') - Date.parse(new Date().toISOString());
    const rawSeconds = Math.floor((total / 1000) % 60);
    const rawMinutes = Math.floor((total / 1000 / 60) % 60);
    const rawHours = Math.floor((total / (1000 * 60 * 60)) % 24);
    const rawDays = Math.floor(total / (1000 * 60 * 60 * 24));
    const seconds = Math.max(rawSeconds, 0);
    const minutes = Math.max(rawMinutes, 0);
    const hours = Math.max(rawHours, 0);
    const days = Math.max(rawDays, 0);
    const isPast = total < 0;
    const isLaunching = total > -(60 * 60) && total < 60 * 60;
    let launchState = '';
    if (isLaunching) {
        launchState = isPast ? 'Just Launched' : 'Launching Soon';
    }
    else if (isPast) {
        launchState = 'Past';
    }
    else {
        launchState = 'Upcoming';
    }
    const calendarDate = DateTime.fromISO(isoDateString || '').toFormat('MMM d, y');
    let shortDescription = '';
    if (!isPast) {
        if (days > 30) {
            shortDescription = `${calendarDate}`;
        }
        else if (!isLaunching) {
            shortDescription =
                days > 0
                    ? `${days} ${days > 1 || days === 0 ? 'days' : 'day'}`
                    : `${hours} ${hours > 1 || hours === 0 ? 'hours' : 'hour'}`;
        }
        else {
            shortDescription = `${zeroPad(days, 2)}:${zeroPad(hours, 2)}:${zeroPad(minutes, 2)}:${zeroPad(seconds, 2)}`;
        }
    }
    else {
        if (isLaunching) {
            shortDescription = '00:00:00:00';
        }
        else if (rawDays > -30) {
            const daysAgo = Math.abs(rawDays);
            const hoursAgo = Math.abs(rawHours);
            shortDescription =
                daysAgo > 1
                    ? `${daysAgo} ${daysAgo > 1 || daysAgo === 0 ? 'days ago' : 'day ago'}`
                    : `${hoursAgo} ${hoursAgo > 1 || hoursAgo === 0 ? 'hours ago' : 'hour ago'}`;
        }
        else {
            shortDescription = `${calendarDate}`;
        }
    }
    return {
        isPast,
        isLaunching,
        shortDescription,
        calendarDate,
        launchState
    };
};
export const countdownToLaunch = (launch, now = new Date()) => {
    const launchDate = launch?.launchInfo?.launchDate?.utc;
    const secondsRaw = launch?.launchInfo?.launchSeconds;
    const launchSeconds = secondsRaw ? parseInt(secondsRaw, 10) : 0;
    const launchDatePrecise = launchDate
        ? new Date(Date.parse(launchDate) + launchSeconds * 1000)
        : new Date();
    const components = countdownToDate(launchDatePrecise, now);
    components.launchSeconds = launchSeconds;
    const overrideLaunchDate = launch?.launchInfo?.overrideLaunchDate;
    const overrideLaunchTime = launch?.launchInfo?.overrideLaunchTime;
    components.isVague =
        (overrideLaunchDate !== undefined && overrideLaunchDate.length > 0) ||
            (overrideLaunchTime !== undefined && overrideLaunchTime.length > 0);
    return components;
};
//# sourceMappingURL=launches.js.map